// Живая цена набранного запроса — отдельным concern'ом от бара: состав каналов
// уезжает вместе с комбинацией входов, потому что цену считает тот же гейт фич,
// что выбирает исполнителя. Иначе бэкенд посчитал бы цену запроса, который с
// этими входами никто не исполнит.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
    isInputsNotExecutable,
    quotePromptLength,
    useQuotePrice,
} from "~/features/send-message/useQuotePrice";

import { formatCostLabel } from "./prompt-cost";

export interface UsePromptQuoteParams {
    /**
     * Набранный промпт. Его длина — такой же признак исполнимости, как число
     * картинок: у строки каталога свой потолок промпта, и исполнителя отбирают
     * по нему.
     */
    text: string;
    inputImageCount: number;
    hasLastFrame: boolean;
    referenceImageCount: number;
    elementCount: number;
    inputVideoCount: number;
    /**
     * Длина приложенного видео влияет не на выбор исполнителя, а на саму цену:
     * тариф мультимодального режима считает вход вместе с выходом. Меряет
     * браузер — у сервера файла до отправки нет.
     */
    inputVideoDurationSeconds: number | null;
}

export interface UsePromptQuoteResult {
    /** Готовая подпись кнопки отправки — цена или голый ярлык генерации. */
    costLabel: string;
    /**
     * Цена отказала именно из-за набранного состава входов. Это не «цена
     * неизвестна»: генерация с такими входами не запустится, поэтому кнопку
     * гасим и говорим, почему. Прочие отказы (цены в каталоге нет) кнопку не
     * трогают — там пользователю менять нечего, и он вправе попробовать.
     */
    inputsNotExecutable: boolean;
}

export function usePromptQuote({
    text,
    inputImageCount,
    hasLastFrame,
    referenceImageCount,
    elementCount,
    inputVideoCount,
    inputVideoDurationSeconds,
}: UsePromptQuoteParams): UsePromptQuoteResult {
    const { t } = useTranslation("chat");

    const promptLength = quotePromptLength(text);
    const quoteInputs = useMemo(
        () => ({
            inputImageCount,
            hasLastFrame,
            referenceImageCount,
            elementCount,
            inputVideoCount,
            inputVideoDurationSeconds,
            promptLength,
        }),
        [
            inputImageCount,
            hasLastFrame,
            referenceImageCount,
            elementCount,
            inputVideoCount,
            inputVideoDurationSeconds,
            promptLength,
        ],
    );
    const priceQuery = useQuotePrice(quoteInputs);

    const costLabel = useMemo(
        () => formatCostLabel(priceQuery.data?.totalPriceUsd, t("prompt.generate")),
        [priceQuery.data, t],
    );

    return { costLabel, inputsNotExecutable: isInputsNotExecutable(priceQuery.error) };
}
